import {createSelector} from 'reselect';
import {or, find, propEq, omit} from 'ramda';

import type {ApplicationStoreType, FilterDataType, FilterItemType} from 'constants/flow/flowTypes';
import type {FilterType, ActiveFilterType} from 'reducers/category/filters/filtersType';
import {
    HTO_CODE_FILTER,
    PRODUCT_BADGE_CODE_FILTER,
    CLEARANCE_PRODUCT_BADGE_FILTER,
} from 'constants/category';

/**
 * Get current filters
 * @param {ApplicationStoreType} store - Application store
 * @returns {FilterType} Current filters
 */
export const getFilters = (store: ApplicationStoreType): FilterType => store.filters;

/**
 * Get active Filters
 * @param {ApplicationStoreType} store - Application store
 * @returns {ActiveFilterType} Active filters
 */
export const getActiveFilters = (store: ApplicationStoreType): ActiveFilterType =>
    store.activeFilters;

/**
 * Get status "filter is active" by code
 * @param {ApplicationStoreType} store - Current store
 * @param {string} codeFilter - Code of filter item for check
 * @return {boolean} Status "filter is active"
 */
export const isActiveFilterByCode = createSelector(
    getActiveFilters,
    (_, codeFilter: string) => codeFilter,
    (activeFilters: {[string]: FilterDataType}, codeFilter: string): boolean =>
        Object.keys(activeFilters).includes(codeFilter),
);

/**
 * Is the Clearance filter active
 * @param {ApplicationStoreType} store - Current store
 * @return {boolean} Status "Clearance filter"
 */
export const isClearanceFilterActive = createSelector(
    getActiveFilters,
    (activeFilters: {[string]: FilterDataType}): boolean =>
        Boolean(
            activeFilters &&
                activeFilters[PRODUCT_BADGE_CODE_FILTER]?.some(
                    ({value}) => value === CLEARANCE_PRODUCT_BADGE_FILTER,
                ),
        ),
);

/**
 * Get filter item by code
 * @param {ApplicationStoreType} store - Current store
 * @param {string} codeFilter - Code of filter item for get
 * @return {FilterItemType | {}} Filter item
 */
export const getFilterItemByCode = createSelector(
    getFilters,
    (_, codeFilter: string) => codeFilter,
    ({items}: FilterDataType, codeFilter: string): FilterItemType | {} =>
        or(find(propEq('code', codeFilter))(items), {}),
);

/**
 * Get a map of available filters
 * @param {ApplicationStoreType} store - Current store
 * @return {Array<string>} Map of available filters
 */
export const getMapAvailableFilters = createSelector(getFilters, ({items}: FilterDataType): {
    [string]: string,
} => (items || []).reduce((acc, {code, title}) => ({...acc, [code]: title}), {}));

/**
 * Get a list of title for active filters excluding hto
 * @param {ApplicationStoreType} store - Current store
 * @return {Array<string>} List of title for active filters excluding hto
 */
export const getFiltersTitleList = createSelector(
    getActiveFilters,
    getMapAvailableFilters,
    (activeFilters: ActiveFilterType, mapFilters: {[string]: string}): Array<string> =>
        Object.keys(omit([HTO_CODE_FILTER], activeFilters)).reduce(
            (acc, item) => (mapFilters[item] ? [...acc, mapFilters[item]] : acc),
            [],
        ),
);

/**
 * Are category filters loaded?
 * @param {ApplicationStoreType} store - Current store
 * @returns {boolean} loaded flag
 */
export const getIsFiltersDataFulfilled = (store: ApplicationStoreType) => {
    return getFilters(store).status === 'fulfilled';
};
